import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { useToast } from "@/components/feedback";
import {
  invalidateParentPortalBalanceAfterPayment,
  scheduleParentPortalBalancePaymentRefetch,
} from "@/features/balance/utils/invalidate-member-balance-payment-queries";
import { useAccessToken, useTenantData } from "@/hooks";
import { createAuthenticatedApi } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import { toRequestCurrency } from "@/lib/utils/currency";
import type { ReleaseFamilyPaymentRequest } from "@/openapi/generated";
import { PaymentApi } from "@/openapi/generated";

export const usePaymentReleaseFamily = (toast: ReturnType<typeof useToast>) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { tenantId, tenantData } = useTenantData();

  return useMutation({
    mutationFn: async (
      releaseFamilyPaymentRequest: ReleaseFamilyPaymentRequest,
    ) => {
      if (!accessToken) throw new Error("Authentication token is required");
      const service = createAuthenticatedApi(PaymentApi, accessToken);
      await service.apiPaymentsReleaseFamilyPaymentPost({
        releaseFamilyPaymentRequest: {
          ...releaseFamilyPaymentRequest,
          currency: toRequestCurrency(
            releaseFamilyPaymentRequest.currency || tenantData?.currency,
          ),
        },
        xTenantID: tenantId,
      });
      return releaseFamilyPaymentRequest;
    },
    onSuccess: async (request) => {
      const userId = Number(request.userId);
      await invalidateParentPortalBalanceAfterPayment(queryClient, userId);
      scheduleParentPortalBalancePaymentRefetch(queryClient, userId);
      toast({
        title: "Payment released",
        description: "Your payment has been submitted and is being processed.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error) => {
      const message = await getApiErrorMessage(
        error,
        "Failed to release payment",
      );
      toast({
        title: "Payment failed",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });
};
